import type { ContractResponse } from '../types';
import type { ContractItemsSyncPayload } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { zodContractResponseSchema } from '../validations';

import { zodContractItemsSyncPayloadSchema } from './validations';

/**
 * Synchronizes the complete active item set of a draft contract atomically.
 *
 * @param contractId - The UUID of the contract to sync items for.
 * @param payload - The complete desired item set.
 * @returns The updated contract.
 * @throws Error if the API request fails. Validation errors are logged but do not throw.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/sync_contract_items_contracts__contract_id__items_sync_post
 */
export const syncContractItems = async (
  contractId: UUID,
  payload: ContractItemsSyncPayload,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.CONTRACT_ITEM.SYNC(contractId), {
      json: payload,
      validate: {
        body: zodContractItemsSyncPayloadSchema,
      },
    })
    .json<ContractResponse>();

  const validatedResponse = zodContractResponseSchema.safeParse(response);
  if (!validatedResponse.success) {
    log.error({
      message: 'Sync contract items API response has validation errors',
      error: {
        errors: validatedResponse.error.issues,
        errorMessage: validatedResponse.error.message,
        response,
      },
    });
    return response;
  }

  return validatedResponse.data;
};
